import { useState } from 'react';
import type { ItemAddition, ItemDraft } from '../../hooks/useOrderEditState';
import type { ItemShape } from '../../lib/orders';
import {
  MAX_BULK_ADD,
  PRIMARY_OPTIONS,
  PRIMARY_SKU_REINIGEN,
  getDefaultPackageForPrimary,
  getPackagesForPrimary,
  isPerStukPrimary,
} from '../../lib/order-edit-rules';

// De Bewerken-flow (drafts + pricing-preview) is gebouwd op m²-producten met
// vorm en pakket. Per-stuk producten (Kussen) gaan via het snel-toevoegen-
// dialoog op de orderpagina; hier bieden we ze niet aan.
const M2_PRIMARY_OPTIONS = PRIMARY_OPTIONS.filter((p) => !isPerStukPrimary(p.sku));

interface AddItemFormProps {
  onAdd: (addition: ItemAddition) => void;
  onCancel: () => void;
}

const EMPTY: ItemDraft = {
  vorm: 'rechthoekig',
  lengteCm: null,
  breedteCm: null,
  diameterCm: null,
  oppervlakteM2: null,
  primarySku: PRIMARY_SKU_REINIGEN,
  packageSku: getDefaultPackageForPrimary(PRIMARY_SKU_REINIGEN),
  ondertapijt: false,
};

export function AddItemForm({ onAdd, onCancel }: AddItemFormProps) {
  const [draft, setDraft] = useState<ItemDraft>(EMPTY);
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const patch = (p: Partial<ItemDraft>) => setDraft((d) => ({ ...d, ...p }));
  const packagesForPrimary = getPackagesForPrimary(draft.primarySku);

  const handlePrimaryChange = (primarySku: string) => {
    patch({
      primarySku,
      packageSku: getDefaultPackageForPrimary(primarySku),
    });
  };

  const handleAdd = () => {
    if (!draft.packageSku) {
      setError('Kies een pakket');
      return;
    }
    if (draft.vorm === 'rechthoekig' && (!draft.lengteCm || !draft.breedteCm)) {
      setError('Vul lengte en breedte in');
      return;
    }
    if (draft.vorm === 'rond' && !draft.diameterCm) {
      setError('Vul diameter in');
      return;
    }
    if (draft.vorm === 'overig' && !draft.oppervlakteM2) {
      setError('Vul oppervlakte in');
      return;
    }
    setError(null);
    const baseTs = Date.now();
    const rnd = Math.random().toString(36).slice(2, 7);
    for (let i = 0; i < quantity; i++) {
      onAdd({ ...draft, tempId: `add-${baseTs}-${i}-${rnd}` });
    }
    setDraft(EMPTY);
    setQuantity(1);
  };

  return (
    <div className="border border-emerald-200 bg-emerald-50/40 rounded-lg p-4 my-3">
      <h3 className="text-sm font-semibold text-slate-900 mb-3">+ Nieuw kleed toevoegen</h3>

      <div className="flex flex-wrap items-center gap-4 mb-3">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Product</span>
        {M2_PRIMARY_OPTIONS.map((p) => (
          <label key={p.sku} className="flex items-center gap-1.5 text-sm">
            <input
              type="radio"
              name="primary-product"
              checked={draft.primarySku === p.sku}
              onChange={() => handlePrimaryChange(p.sku)}
            />
            {p.label}
          </label>
        ))}
      </div>

      <div className="flex items-center gap-4 mb-3">
        {(['rechthoekig', 'rond', 'overig'] as ItemShape[]).map((s) => (
          <label key={s} className="flex items-center gap-1.5 text-sm">
            <input
              type="radio"
              checked={draft.vorm === s}
              onChange={() => patch({ vorm: s })}
            />
            {s === 'rechthoekig' ? 'Rechthoek' : s === 'rond' ? 'Rond' : 'Overig'}
          </label>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
        {draft.vorm === 'rechthoekig' && (
          <>
            <FieldInput
              label="Lengte (cm)"
              value={draft.lengteCm}
              onChange={(v) => patch({ lengteCm: v })}
            />
            <FieldInput
              label="Breedte (cm)"
              value={draft.breedteCm}
              onChange={(v) => patch({ breedteCm: v })}
            />
          </>
        )}
        {draft.vorm === 'rond' && (
          <FieldInput
            label="Diameter (cm)"
            value={draft.diameterCm}
            onChange={(v) => patch({ diameterCm: v })}
          />
        )}
        {draft.vorm === 'overig' && (
          <FieldInput
            label="Oppervlakte (m²)"
            value={draft.oppervlakteM2}
            onChange={(v) => patch({ oppervlakteM2: v })}
            step={0.1}
          />
        )}
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-3">
        <label className="flex flex-col text-xs text-slate-500 gap-1">
          Pakket
          <select
            value={draft.packageSku ?? ''}
            onChange={(e) => patch({ packageSku: e.target.value || null })}
            className="border border-slate-300 rounded px-2 py-1 text-sm bg-white"
          >
            {packagesForPrimary.map((p) => (
              <option key={p.sku} value={p.sku}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-xs text-slate-500 gap-1">
          Aantal
          <input
            type="number"
            min={1}
            max={MAX_BULK_ADD}
            step={1}
            value={quantity}
            onChange={(e) => {
              const n = Math.max(1, Math.min(MAX_BULK_ADD, Number(e.target.value) || 1));
              setQuantity(n);
            }}
            className="border border-slate-300 rounded px-2 py-1 text-sm bg-white w-20"
          />
        </label>
        <label className="flex items-center gap-1.5 text-sm pt-4">
          <input
            type="checkbox"
            checked={draft.ondertapijt}
            onChange={(e) => patch({ ondertapijt: e.target.checked })}
          />
          Ondertapijt
        </label>
      </div>

      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleAdd}
          className="px-3 py-1.5 text-sm font-medium bg-emerald-600 text-white rounded hover:bg-emerald-700"
        >
          {quantity > 1 ? `Toevoegen (${quantity})` : 'Toevoegen'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900"
        >
          Annuleren
        </button>
      </div>
    </div>
  );
}

function FieldInput({
  label,
  value,
  onChange,
  step,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  step?: number;
}) {
  return (
    <label className="flex flex-col text-xs text-slate-500 gap-1">
      {label}
      <input
        type="number"
        min={0}
        step={step ?? 1}
        value={value ?? ''}
        onChange={(e) => {
          const n = e.target.value === '' ? null : Number(e.target.value);
          onChange(Number.isFinite(n as number) ? (n as number) : null);
        }}
        className="border border-slate-300 rounded px-2 py-1 text-sm bg-white"
      />
    </label>
  );
}
